import type { Node } from "reactflow";
import type { InsightNodeData } from "./topologyNodes";
import { DEFAULT_PER_SUBNET_ENI_LIMIT } from "./eniDisclosure";
import { eniGridSlot, type EniGridMeta } from "./cardLayoutGrid";

/**
 * Client-side card-dimension normalization for the digest's ELK boxes.
 *
 * insight-data-service's layout profile sizes leaf cards for a minimal
 * label+id rendering (a 200×50 ENI, a 240×70 route table). The TopologyV2
 * semantic cards render more than that — an ENI card shows its IPs, its
 * resolved instance and its security-group chips; a route table shows its
 * aggregated connectivity chips — so the content visibly overflows/clips the
 * server box ("the ENI box is malformed"). This module fixes the boxes once,
 * right after `toReactFlowNodes` maps the digest:
 *
 * - Every ENI card gets the uniform `ENI_CARD_WIDTH`×`ENI_CARD_HEIGHT` box
 *   the full-detail renderer actually needs, and each subnet's interior is
 *   re-gridded for the new card size (the server's own contract — a subnet
 *   box sized for ALL its ENIs — is preserved, so expand/collapse still
 *   never resizes the subnet). The grid geometry is stored on the subnet's
 *   `data.eniGrid` so `eniDisclosure` can place its "+N ENIs"/"Show fewer"
 *   card in a real free slot instead of on top of an ENI.
 * - Route-table/gateway cards get a content-driven minimum height
 *   (connectivity chips need more room than a bare label).
 * - Every resized card's data is tagged `cardLayout: "normalized"` —
 *   `applyHierarchyDisclosure` uses that tag to know the ELK sibling
 *   positions are stale and a dense repack is required even below the
 *   visible-count caps.
 *
 * Pure and reference-preserving: nodes that already fit are returned as the
 * same object, and a node list needing no change at all is returned as the
 * same array reference (legacy fixtures render byte-for-byte identically).
 */

export const ENI_CARD_WIDTH = 184;
/** Compact full-detail ENI card: label + IP line(s) + optional instance line + one SG chip row (the native id renders only when it differs from the label, and "standalone" is the unmarked default). */
export const ENI_CARD_HEIGHT = 88;
export const SUBNET_PADDING_X = 12;
/** Room for the SUBNET corner tag + label/id/CIDR header text before the first ENI row. */
// Must clear the subnet header's full text stack (32px top offset + name +
// id + CIDR ≈ 71px) PLUS the ~10px the first ENI row's corner icon badge
// overhangs ABOVE its card — at 76 the badges sat right on the CIDR line.
export const SUBNET_HEADER_HEIGHT = 96;
export const SUBNET_PADDING_BOTTOM = 16;
export const ENI_GRID_GAP = 12;
/**
 * ENI columns every subnet card lays its interior out in, and therefore the one
 * width every subnet card has. Two is the balance point: one column makes an
 * eight-ENI subnet (the per-subnet disclosure cap) a ~900px tower, while three or
 * more leaves most cards mostly empty, since real subnets usually hold one or two
 * interfaces.
 */
export const SUBNET_ENI_COLUMNS = 2;
export const ROUTE_TABLE_MIN_HEIGHT = 56;
export const GATEWAY_MIN_HEIGHT = 56;

export { eniGridSlot, type EniGridMeta } from "./cardLayoutGrid";

function withBox(
	node: Node<InsightNodeData>,
	box: { x?: number; y?: number; width?: number; height?: number },
	extraData?: Record<string, unknown>
): Node<InsightNodeData> {
	const width = box.width ?? node.width;
	const height = box.height ?? node.height;
	return {
		...node,
		position: box.x != null && box.y != null ? { x: box.x, y: box.y } : node.position,
		width,
		height,
		style: { ...node.style, width, height },
		data: extraData ? { ...node.data, ...extraData } : node.data,
	};
}

function clampLeafHeight(node: Node<InsightNodeData>, minHeight: number): Node<InsightNodeData> | null {
	if (typeof node.height === "number" && node.height >= minHeight) return null;
	return withBox(node, { height: minHeight }, { cardLayout: "normalized" });
}

export function normalizeCardDimensions(nodes: Node<InsightNodeData>[]): Node<InsightNodeData>[] {
	const updates = new Map<string, Node<InsightNodeData>>();

	const childrenByParent = new Map<string, Node<InsightNodeData>[]>();
	nodes.forEach((node) => {
		if (!node.parentId) return;
		const list = childrenByParent.get(node.parentId) ?? [];
		list.push(node);
		childrenByParent.set(node.parentId, list);
	});

	nodes.forEach((node) => {
		if (node.type === "insightRouteTable") {
			// Gateway associations render as icon chips on the card's BORDER, so
			// connectivity never needs extra card height.
			const clamped = clampLeafHeight(node, ROUTE_TABLE_MIN_HEIGHT);
			if (clamped) updates.set(node.id, clamped);
			return;
		}
		if (node.type === "insightGateway") {
			const clamped = clampLeafHeight(node, GATEWAY_MIN_HEIGHT);
			if (clamped) updates.set(node.id, clamped);
			return;
		}

		if (node.type !== "insightSubnet") return;
		const children = childrenByParent.get(node.id) ?? [];
		if (children.length === 0 || children.some((child) => child.type !== "insightEni")) return;
		const alreadySized = children.every(
			(eni) => eni.width === ENI_CARD_WIDTH && typeof eni.height === "number" && eni.height >= ENI_CARD_HEIGHT
		);
		if (alreadySized) return;

		// One extra slot reserves room for eniDisclosure's overflow/"Show fewer"
		// card so it never lands on top of an ENI, and the subnet's box stays
		// constant across expand/collapse (CTX-128: disclosure never moves the
		// camera or the siblings).
		const slots = children.length + (children.length > DEFAULT_PER_SUBNET_ENI_LIMIT ? 1 : 0);
		// Deliberately NOT derived from the server's box or from the ENI count:
		// every subnet card is the same width so an AZ's subnets form real
		// columns. Sizing each card around its own ENI grid made a one-ENI subnet
		// ~210px and a three-ENI one ~600px, and any grid of those is ragged —
		// rows of unequal width, nothing vertically aligned, and the AZ forced to
		// hug its widest row while the narrow ones trailed dead space.
		const columns = SUBNET_ENI_COLUMNS;
		const rows = Math.ceil(slots / columns);
		const meta: EniGridMeta = {
			originX: SUBNET_PADDING_X,
			originY: SUBNET_HEADER_HEIGHT,
			columns,
			cardWidth: ENI_CARD_WIDTH,
			cardHeight: ENI_CARD_HEIGHT,
			gapX: ENI_GRID_GAP,
			gapY: ENI_GRID_GAP,
		};

		children.forEach((eni, index) => {
			const slot = eniGridSlot(meta, index);
			updates.set(eni.id, withBox(eni, { ...slot, width: ENI_CARD_WIDTH, height: ENI_CARD_HEIGHT }));
		});

		const width = 2 * SUBNET_PADDING_X + columns * (ENI_CARD_WIDTH + ENI_GRID_GAP) - ENI_GRID_GAP;
		const height = SUBNET_HEADER_HEIGHT + rows * (ENI_CARD_HEIGHT + ENI_GRID_GAP) - ENI_GRID_GAP + SUBNET_PADDING_BOTTOM;
		updates.set(node.id, withBox(node, { width, height }, { cardLayout: "normalized", eniGrid: meta }));
	});

	if (updates.size === 0) return nodes;
	return nodes.map((node) => updates.get(node.id) ?? node);
}
